import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cloneElement, forwardRef, isValidElement, useContext } from 'react';
import { asArray } from '@olivierpascal/helpers';
import { isFunction } from '../../helpers/isFunction';
import { useStyles } from '../../hooks/useStyles';
import { iconCheckMark, iconExclamationTriangle } from '../../assets/icons';
import { StepperContext } from '../Stepper';
import { IndeterminateCircularProgressIndicator } from '../IndeterminateCircularProgressIndicator';
import { ButtonBase } from '../ButtonBase';
import { SvgIcon } from '../SvgIcon';
import { Base } from '../Base';
import { Stack } from '../Stack';
import { stepCircularProgressIndicatorStyles, stepFocusRingStyles, stepStyles, } from './Step.styles';
import { stepTheme } from './Step.stylex';
import { StepContext } from './Step.context';
export const Step = forwardRef(function Step(props, forwardedRef) {
    const { styles, sx, innerStyles, active: activeProp, completed: completedProp, disabled, index = 0, last: isLast, icon, label, supportingText, hasError, loading: loadingProp, orientation: orientationProp, labelPosition: labelPositionProp, nextConnector: nextConnectorProp, alwaysExpanded, children, onClick, ...other } = props;
    const { combineStyles, getStyles, globalStyles } = useStyles({
        name: 'Step',
        styles: [stepStyles, styles],
    });
    const context = useContext(StepperContext);
    const hasText = !!label || !!supportingText;
    const completed = !disabled &&
        (context?.completed ||
            (completedProp ??
                (context?.activeStep !== undefined && index < context.activeStep)));
    const previousCompleted = !disabled &&
        context?.activeStep !== undefined &&
        index <= context.activeStep;
    const isActive = !disabled && (activeProp ?? (context && index === context.activeStep));
    const loading = context?.loading || loadingProp;
    const labelPosition = hasText
        ? (labelPositionProp ?? context?.labelPosition ?? 'right')
        : 'right';
    const nextConnector = nextConnectorProp !== undefined ? nextConnectorProp : context?.connector;
    const orientation = orientationProp ?? context?.orientation ?? 'horizontal';
    const expanded = orientation === 'vertical' && !!children && (isActive || alwaysExpanded);
    const isFirst = index === 0;
    const state = disabled
        ? 'disabled'
        : hasError
            ? 'error'
            : completed
                ? 'completed'
                : !isActive
                    ? 'inactive'
                    : undefined;
    const contextValue = {
        completed,
        hasContent: expanded,
        hasText,
        orientation,
        labelPosition,
    };
    const renderButtonInner = (isInteractive) => (_jsxs(Stack, { horizontal: true, gap: 2, sx: combineStyles(labelPosition === 'right' && 'buttonInner$rightLabel', labelPosition === 'bottom' && 'buttonInner$bottomLabel'), children: [loading || icon || hasError ? (_jsx("div", { ...getStyles('bulletPoint'), children: _jsx("div", { ...getStyles('icon', state && `icon$${state}`), children: loading ? (_jsx(IndeterminateCircularProgressIndicator, { styles: [
                            stepCircularProgressIndicatorStyles,
                            ...asArray(innerStyles?.circularProgressIndicator),
                        ] })) : ((icon ??
                        (hasError ? _jsx(SvgIcon, { icon: iconExclamationTriangle }) : null))) }) })) : (_jsxs("div", { ...getStyles('bulletPoint', 'bulletPoint$container'), children: [_jsx("div", { ...getStyles('background', state && `background$${state}`) }), _jsx("div", { ...getStyles('text', state && `text$${state}`), children: completed ? _jsx(SvgIcon, { icon: iconCheckMark }) : index + 1 })] })), hasText ? (_jsxs(Stack, { sx: combineStyles(labelPosition === 'right' && `labelContainer$rightLabel`, labelPosition === 'bottom' && `labelContainer$bottomLabel`), children: [label ? (_jsx("div", { ...getStyles('label', isInteractive && 'label$interactive', state && `label$${state}`), children: label })) : null, supportingText ? (_jsx("div", { ...getStyles('supportingText', isInteractive && 'supportingText$interactive', state && `supportingText$${state}`), children: supportingText })) : null] })) : null] }));
    const renderConnectorWithoutChildren = () => isValidElement(nextConnector)
        ? cloneElement(nextConnector, { children: undefined })
        : null;
    // As the step height may change depending on the content, in a vertical
    // orientation, we need to add inner top and bottom connectors in order to
    // connect the bullet point to the previous and next elements.
    const renderInnerConnectors = () => (_jsxs(_Fragment, { children: [!isFirst ? (
            // This connector must be rendered in the context of the previous
            // step.
            _jsx(StepContext.Provider, { value: { ...contextValue, completed: previousCompleted }, children: _jsx("div", { ...getStyles('connectorContainer', 'connectorContainer$top'), children: renderConnectorWithoutChildren() }) })) : null, !isLast ? (_jsx("div", { ...getStyles('connectorContainer', 'connectorContainer$bottom'), children: renderConnectorWithoutChildren() })) : null] }));
    return (_jsxs(StepContext.Provider, { value: contextValue, children: [_jsxs(Base, { ...other, sx: [
                    stepTheme,
                    globalStyles,
                    combineStyles('host', labelPosition === 'bottom' && `host$bottomLabel`),
                    sx,
                ], ref: forwardedRef, children: [_jsxs("div", { ...getStyles('buttonContainer', labelPosition === 'bottom' && 'buttonContainer$bottomLabel'), children: [orientation === 'vertical' ? renderInnerConnectors() : null, onClick ? (_jsx(ButtonBase, { sx: combineStyles('button'), innerStyles: {
                                    ...innerStyles,
                                    focusRing: [
                                        stepFocusRingStyles,
                                        ...asArray(innerStyles?.focusRing),
                                    ],
                                }, onClick: onClick, disabled: disabled, children: renderButtonInner(true) })) : (_jsx("div", { ...getStyles('button'), children: renderButtonInner(false) }))] }), expanded ? (_jsxs("div", { ...getStyles('content'), children: [!isLast ? (_jsx("div", { ...getStyles('connectorContainer', 'connectorContainer$content'), children: renderConnectorWithoutChildren() })) : null, _jsx("div", { ...getStyles('contentText'), children: isFunction(children)
                                    ? children({
                                        active: !!isActive,
                                        completed,
                                        hasError: !!hasError,
                                    })
                                    : children })] })) : null, !isLast &&
                        orientation === 'horizontal' &&
                        labelPosition === 'bottom' ? (_jsx("div", { ...getStyles('connectorContainer', 'connectorContainer$horizontal$bottomLabel'), children: nextConnector })) : null] }), !isLast && labelPosition === 'right' ? (_jsx("div", { ...getStyles('extensibleConnectorContainer', orientation === 'horizontal' &&
                    'extensibleConnectorContainer$horizontal', orientation === 'vertical' &&
                    'extensibleConnectorContainer$vertical'), children: _jsx("div", { ...getStyles('connectorContainer', orientation === 'horizontal' &&
                        'connectorContainer$horizontal$rightLabel'), children: nextConnector }) })) : null] }));
});
Step.displayName = 'Step';
